import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { Feature } from '@/config/permissions'
import { fieldsOfGroup } from '@/composables/useFieldGroups'
import { UserRole } from '@/types'

export function usePermissions() {
  const authStore = useAuthStore()

  const userRole = computed(() => authStore.user?.role)
  const isDisabled = computed(() => !!authStore.user?.is_disabled)

  /** Delega allo store: la stessa regola che applica la guardia del router.
   *  Due implementazioni erano gia' divergite una volta. */
  const canAccess = (feature: Feature): boolean => authStore.canAccess(feature)

  /**
   * Se un campo è modificabile dal ruolo collegato.
   *
   * `authStore.writeGroups` è `null` per un ruolo fisso, che non è limitato per
   * campo: in quel caso decide `canEdit` come prima. Per un ruolo personalizzato
   * il campo passa solo se sta in uno dei gruppi che possiede.
   *
   * È una cortesia, non una difesa: il backend rifiuta comunque
   * (assert_writable). Serve a non offrire un campo che risponderebbe 403.
   */
  const canWriteField = (entity: string, field: string): boolean => {
    if (isDisabled.value) return false
    const groups = authStore.writeGroups
    if (groups === null || groups === undefined) return roleWritesEntity(entity)
    const held = groups[entity] ?? []
    return held.some(group => fieldsOfGroup(entity, group).includes(field))
  }

  /**
   * I ruoli fissi che il backend accetta in scrittura su un'entità, quando non
   * sono i soliti admin e tech_user.
   *
   * Sui contratti `require_write` ammette anche `bco_user` (WRITE_ROLES in
   * `src/api/contracts.py`): il back office commerciale è chi registra le
   * vendite. Senza questa mappa la pagina nascondeva a bco_user pulsanti che il
   * backend gli avrebbe accettato.
   */
  const ENTITY_WRITE_ROLES: Record<string, UserRole[]> = {
    contract: [UserRole.ADMIN, UserRole.TECH_USER, UserRole.BCO_USER]
  }

  /** Se un ruolo fisso può scrivere su questa entità. */
  const roleWritesEntity = (entity: string): boolean => {
    if (isDisabled.value) return false
    const roles = ENTITY_WRITE_ROLES[entity]
    if (!roles) return canEdit.value
    return roles.includes(userRole.value as UserRole)
  }

  /** Se il ruolo può modificare almeno un campo di un'entità: decide se ha senso
   *  mostrargli il pulsante Modifica. */
  const canEditEntity = (entity: string): boolean => {
    if (isDisabled.value) return false
    const groups = authStore.writeGroups
    if (groups === null || groups === undefined) return roleWritesEntity(entity)
    return (groups[entity] ?? []).length > 0
  }

  /** Il backend chiede il ruolo, non il permesso, per scrivere ed eliminare
   *  (require_roles in services/accounts/assignments/rulesets). Un ruolo
   *  personalizzato non passa quei controlli: offrire il pulsante comunque
   *  significherebbe mostrarne uno che risponde 403. */
  const isAdmin = computed(() => userRole.value === UserRole.ADMIN)
  const canEdit = computed(() =>
    userRole.value === UserRole.ADMIN || userRole.value === UserRole.TECH_USER ||
    userRole.value === UserRole.BCO_USER
  )
  /** La fatturazione e i listini li scrive admin o BCO, non `tech_user`: serve
   *  quindi distinguere il ruolo, non basta `canEdit`. Eliminare un listino, un
   *  catalogo o una condizione resta agli admin (`isAdmin`). */
  const isBcoUser = computed(() => userRole.value === UserRole.BCO_USER)

  /** I gruppi posseduti, per spiegare a video cosa si può modificare. `null`
   *  quando il ruolo non è limitato per campo. */
  const writeGroups = computed(() => authStore.writeGroups ?? null)

  return {
    canAccess,
    isDisabled,
    isAdmin,
    isBcoUser,
    canEdit,
    canWriteField,
    canEditEntity,
    roleWritesEntity,
    writeGroups,
    userRole,
  }
}
